/* Поле с чипами «Текста» (BotCore.html.box, §1.5 и §1.7): подпись, счётчик, «во весь экран», чипы, «Улучшить» (если доступно), подпись-правило.
   «Улучшить» — POST /app/text/improve (03-api §7.7), бесплатно и без заказа; кнопка стоит, только когда политика формы
   публикует `improve_available: true` — иначе её нет. */
import { useState } from 'react';
import { api } from '../../../api';
import { uiFormat, uiText } from '../../../i18n';
import { PromptChip } from '../../../ui/PromptChip';
import { TextEditor } from '../../../ui/TextEditor';
import { TextField } from '../../../ui/TextField';
import { Sheet, SheetHead } from '../../../ui/Sheet';
import { StudioIcon } from '../../studio/StudioIcon';

export interface TextChip {
  label: string;
  on: boolean;
  press(): void;
}
export function TextImprove({ kind, text, max, disabled, onPick, field }: { kind: string; text: string; max: number; disabled: boolean; onPick(value: string): void; field: string }) {
  const [open, setOpen] = useState(false);
  const [variants, setVariants] = useState<string[] | null>(null);
  const [error, setError] = useState('');
  const [hint, setHint] = useState('');
  const short = Array.from(text.trim()).length < 3;
  async function run() {
    if (short) {
      setHint('Напишите пару слов, и мы предложим варианты');
      return;
    }
    setHint('');
    setOpen(true);
    setVariants(null);
    setError('');
    try {
      const result = await api.request<{ variants?: unknown }>('/app/text/improve', { method: 'POST', body: { kind, text, max } });
      const list = Array.isArray(result.variants)
        ? result.variants.filter((item): item is string => typeof item === 'string' && !!item.trim() && Array.from(item).length <= max).slice(0, 3)
        : [];
      if (!list.length) throw new Error('empty');
      setVariants(list);
    } catch {
      setError('Не получилось улучшить. Измените текст и попробуйте ещё раз.');
      setVariants([]);
    }
  }
  return (
    <>
      <button type="button" className={`bci${short ? ' off' : ''}`} disabled={disabled} aria-disabled={short || undefined} onClick={() => void run()}>
        <StudioIcon kind="sparkle" />
        <span>{uiText('Улучшить')}</span>
      </button>
      {hint && (
        <span className="studio-settings-tip" role="status">
          {uiText(hint)}
        </span>
      )}
      {open && (
        <Sheet id="bcImpText" label={uiText('Улучшить')} open layer onClose={() => setOpen(false)}>
          <div className="cbody">
            <SheetHead title={uiText('Улучшить')} sub={uiFormat('Выберите вариант для поля «{0}»', uiText(field))} onClose={() => setOpen(false)} />
            {variants === null ? (
              <div className="tt-vars" aria-busy="true">
                {[0, 1, 2].map(index => (
                  <div className="tt-var sk" key={index}>
                    <b />
                    <i />
                    <i />
                    <i />
                  </div>
                ))}
              </div>
            ) : (
              <div className="tt-vars">
                {error && (
                  <p className="note bad" role="alert">
                    {uiText(error)}
                  </p>
                )}
                {variants.map(variant => (
                  <button
                    key={variant}
                    type="button"
                    className="tt-rd tt-var text-improve-tile"
                    onClick={() => {
                      onPick(variant);
                      setOpen(false);
                    }}
                  >
                    <span className="tt-p">{variant}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </Sheet>
      )}
    </>
  );
}
export function TextBox({
  label,
  value,
  onChange,
  limit,
  minimum = 0,
  disabled,
  chips = [],
  placeholder,
  note,
  improve,
}: {
  label: string;
  value: string;
  onChange(value: string): void;
  limit: number;
  minimum?: number;
  disabled: boolean;
  chips?: TextChip[];
  placeholder?: string;
  note?: string;
  improve?: { kind: string } | null;
}) {
  const [full, setFull] = useState(false);
  const row = (editor = false) =>
    !!chips.length && (
      <div className={`chips${editor ? ' bc-edch' : ''}`}>
        {chips.map(chip => (
          <PromptChip key={chip.label} label={uiText(chip.label)} selected={chip.on} disabled={disabled} onClick={chip.press} />
        ))}
      </div>
    );
  const improveButton = improve && (
    <TextImprove kind={improve.kind} text={value} max={limit} disabled={disabled} field={label} onPick={next => onChange(Array.from(next).slice(0, limit).join(''))} />
  );
  return (
    <div className="photo-field pf-ta text-box">
      <TextField
        label={uiText(label)}
        value={value}
        onChange={onChange}
        minimum={minimum}
        limit={limit}
        disabled={disabled}
        onExpand={() => setFull(true)}
        expandLabel={uiText('Во весь экран')}
        placeholder={placeholder ? uiText(placeholder) : undefined}
      >
        {row()}
        {(improveButton || note) && (
          <div className="foot studio-description-actions">
            {!full && improveButton}
            {note && <span className="pf-fn">{uiText(note)}</span>}
          </div>
        )}
      </TextField>
      {full && (
        <TextEditor
          label={uiFormat('{0} во весь экран', uiText(label))}
          value={value}
          onChange={onChange}
          limit={limit}
          disabled={disabled}
          onClose={() => setFull(false)}
          toolbar={
            improveButton || chips.length ? (
              <>
                {improveButton}
                {row(true)}
              </>
            ) : undefined
          }
        />
      )}
    </div>
  );
}
